"use client"

import { useEffect, useRef, useState } from "react"

// The "What is your time actually worth?" calculator from the approved mockup.
//
// The mock drives this with an inline <script> and getElementById. Same maths,
// same defaults, same rounding, expressed as React state because a Next server
// component cannot ship an inline script and an unmanaged DOM write would fight
// hydration. 2080 is a working year in hours, which is what turns an annual
// income into an hourly rate. These constants match the repo's existing
// CostCalculator exactly, so the two can never quote different numbers.
//
// ONE deliberate addition to the mock: the income figure is click-to-type. That
// is a standing owner law for this control specifically (2026-06-28: "the annual
// income should go up to 5 million and if they click the number, they should
// have an ability to type it in"), and a plain slider cannot reach a precise
// figure across a 50k-to-5M range. It changes no copy, no layout, and nothing
// visible until the number is clicked.
const HOURS_PER_YEAR = 2080
const DAYS_PER_MONTH = 30
const INCOME_MIN = 50_000
const INCOME_MAX = 5_000_000

const money = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`

const clampIncome = (n: number) => Math.min(INCOME_MAX, Math.max(INCOME_MIN, n))

export function TimeCalculator() {
  const [income, setIncome] = useState(250_000)
  const [hours, setHours] = useState(2)
  const [typing, setTyping] = useState(false)
  const [draft, setDraft] = useState("")

  // A drag fires an "input" event on nearly every pixel of pointer motion, far
  // more often than the screen can repaint. Coalescing those into one state
  // update per animation frame keeps the thumb tracking the pointer instead of
  // queuing a React render per tick, which is what read as "laggy" dragging.
  const incomeFrame = useRef<number | null>(null)
  const hoursFrame = useRef<number | null>(null)

  function dragIncome(value: number) {
    if (incomeFrame.current !== null) cancelAnimationFrame(incomeFrame.current)
    incomeFrame.current = requestAnimationFrame(() => {
      incomeFrame.current = null
      setIncome(value)
    })
  }

  function dragHours(value: number) {
    if (hoursFrame.current !== null) cancelAnimationFrame(hoursFrame.current)
    hoursFrame.current = requestAnimationFrame(() => {
      hoursFrame.current = null
      setHours(value)
    })
  }

  useEffect(() => {
    return () => {
      if (incomeFrame.current !== null) cancelAnimationFrame(incomeFrame.current)
      if (hoursFrame.current !== null) cancelAnimationFrame(hoursFrame.current)
    }
  }, [])

  function commitDraft() {
    const parsed = Number(draft.replace(/[^0-9.]/g, ""))
    if (Number.isFinite(parsed) && parsed > 0) setIncome(clampIncome(parsed))
    setTyping(false)
  }

  const rate = income / HOURS_PER_YEAR
  const saved = rate * hours * DAYS_PER_MONTH
  const monthlyHours = Math.round(hours * DAYS_PER_MONTH)

  return (
    <div className="calc">
      <label htmlFor="dfy-income">
        Your annual income
        {typing ? (
          <input
            className="typed"
            type="text"
            inputMode="numeric"
            autoFocus
            aria-label="Type your annual income"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitDraft()
              if (e.key === "Escape") setTyping(false)
            }}
          />
        ) : (
          <output
            htmlFor="dfy-income"
            role="button"
            tabIndex={0}
            title="Click to type an exact figure"
            onClick={() => {
              setDraft(String(income))
              setTyping(true)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                setDraft(String(income))
                setTyping(true)
              }
            }}
          >
            {money(income)}
          </output>
        )}
      </label>
      <input
        id="dfy-income"
        type="range"
        min={INCOME_MIN}
        max={INCOME_MAX}
        step={10_000}
        value={income}
        onChange={(e) => dragIncome(Number(e.target.value))}
      />
      <p className="note">
        That&rsquo;s about <b>{money(rate)}</b>/hr of your time.
      </p>

      <label htmlFor="dfy-hours">
        Hours per day on dating apps and texting <output htmlFor="dfy-hours">{hours} hrs</output>
      </label>
      <input
        id="dfy-hours"
        type="range"
        min={0.5}
        max={6}
        step={0.5}
        value={hours}
        onChange={(e) => dragHours(Number(e.target.value))}
      />

      {/* The primary result is HOURS recovered, never a pseudo-cash savings
          line (owner, 2026-08-20 rebuild: "We'd save you $7,212 every month
          of your time back" is awkward and assumes every hour is literally
          recovered — "The primary result should be hours recovered"). The
          dollar figure supports it in his sentence shape: "At $120/hour,
          that's $7,212 worth of your time spent on swiping and messaging
          each month." */}
      <div className="big">
        <span className="k">You get back</span>
        <b>{monthlyHours} hours a month</b>
        <p className="sub2" style={{ marginTop: 10 }}>
          At {money(rate)}/hour, that&rsquo;s <strong>{money(saved)}</strong> worth of your time spent on
          swiping and messaging each month.
        </p>
      </div>
    </div>
  )
}
